import {attributesToProps, Element as ParserElement} from 'html-react-parser';
import {filter, isArray, isEmpty, split} from 'lodash';
import {Fragment} from 'react';

import {LanguageService} from 'services';
import {ParticipantRole} from 'types';

const isMatchElement = (unit: string, node: ParserElement) => node?.attribs?.unit === unit;
const getParentAsElement = (node: ParserElement) => node?.parentNode as ParserElement;
const isMatchAnyParent = (unit: string, node: ParserElement) => {
    let isMatch = false;
    let parentNode = getParentAsElement(node);
    while (parentNode && !isMatch) {
        isMatch = isMatchElement(unit, parentNode);
        parentNode = getParentAsElement(parentNode);
    }
    return isMatch;
};
const isMatchAnyChild = (unit: string, node: ParserElement) => {
    let isMatch = false;
    let index = 0;
    const children = isArray(node.childNodes) ? [...node.childNodes] : [];
    while (!isMatch && index < children?.length) {
        const childrenCopy = [...children];
        for (let i = 0; i < childrenCopy.length; i++) {
            const child = childrenCopy[i] as ParserElement;
            isMatch = isMatchElement(unit, child);
            if (isMatch) {
                return true;
            }
            if (!isEmpty(child.childNodes)) {
                children.push(...child.childNodes);
            }
            index++;
        }
    }
    return isMatch;
};
const isMatchElementInTree = (unit: string, node: ParserElement) => isMatchAnyParent(unit, node) || isMatchAnyChild(unit, node);

const isMatchLanguage = (node: ParserElement) => {
    const props = attributesToProps(node.attribs);
    const languageWhiteList = filter(split(props['lang-show'] as string), ',');
    const languageBlackList = filter(split(props['lang-hide'] as string, ','));
    const language = LanguageService.getLanguage();

    return isEmpty(languageWhiteList) && isEmpty(languageBlackList) ||
        !isEmpty(languageWhiteList) && languageWhiteList.includes(language) ||
        !isEmpty(languageBlackList) && !languageBlackList.includes(language);
};

const isMatchRole = (node: ParserElement, role: ParticipantRole) => {
    const props = attributesToProps(node.attribs);
    const allowedRole = props.role as ParticipantRole;
    return isEmpty(allowedRole) || allowedRole === role;
};

const renderDefaultNode = (node: ParserElement, options: {role: ParticipantRole}) => {
    if (!isMatchLanguage(node)) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <Fragment />;
    }
    if (!isMatchRole(node, options?.role)) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <Fragment />;
    }
};

export const dynamicContentUtils = {
    isMatchElement,
    isMatchAnyParent,
    isMatchAnyChild,
    isMatchElementInTree,
    // returning null means to render an element as is.
    // returning <Fragment /> means to remove an element from dom.
    // eslint-disable-next-line react/display-name, react/jsx-no-useless-fragment
    nodeElementParent: ({show}: {show: boolean}) => show ? null : <Fragment />,
    renderDefaultNode,
    isMatchLanguage,
    isMatchRole
};